import {
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";
import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
  type CmtsGridRowAction,
} from "@components/data-grid/index.js";
import { CmtsPageHintPanel } from "@components/hints/index.js";

type Row = Record<string, unknown>;
type ExportFormat = "CSV" | "XLS" | "JSON";
type ActionResult = {
  readonly success: boolean;
  readonly title: string;
  readonly message: string;
};

export type CmtsOperationalReadinessPageProps = {
  readonly checks?: readonly Row[] | undefined;
  readonly results?: readonly Row[] | undefined;
  readonly report?: unknown;
  readonly scenarios?: readonly Row[] | undefined;
  readonly diagnostics?: readonly ComponentDiagnostic[] | undefined;
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly onRunCheck?: (row: Row) => void | Promise<void>;
  readonly onRunAll?: () => void | Promise<void>;
  readonly onCreateTask?: (row: Row) => void | Promise<void>;
  readonly onRefresh?: () => void | Promise<void>;
};

const checkColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "code",
    header: "Check Code",
    accessor: readCheckCode,
    sortable: true,
    filterable: true,
    minWidth: 190,
  },
  {
    id: "domain",
    header: "Domain",
    accessor: readDomain,
    sortable: true,
    filterable: true,
    minWidth: 145,
  },
  {
    id: "module",
    header: "Module",
    accessor: readModule,
    sortable: true,
    filterable: true,
    minWidth: 160,
  },
  {
    id: "name",
    header: "Check",
    accessor: readCheckName,
    sortable: true,
    filterable: true,
    minWidth: 250,
  },
  {
    id: "readinessStatus",
    header: "Readiness",
    accessor: readStatus,
    sortable: true,
    filterable: true,
    minWidth: 175,
  },
  {
    id: "validator",
    header: "Validator",
    accessor: readValidator,
    sortable: true,
    filterable: true,
    minWidth: 220,
  },
  {
    id: "remediationSuggestion",
    header: "Remediation",
    accessor: readRemediation,
    sortable: true,
    filterable: true,
    minWidth: 280,
  },
];

const resultColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "ranAt",
    header: "Ran At",
    accessor: readRanAt,
    sortable: true,
    filterable: true,
    minWidth: 180,
  },
  {
    id: "code",
    header: "Result Code",
    accessor: readResultCode,
    sortable: true,
    filterable: true,
    minWidth: 190,
  },
  {
    id: "module",
    header: "Module",
    accessor: readModule,
    sortable: true,
    filterable: true,
    minWidth: 150,
  },
  {
    id: "status",
    header: "Status",
    accessor: readStatus,
    sortable: true,
    filterable: true,
    minWidth: 160,
  },
  {
    id: "score",
    header: "Score",
    accessor: readScore,
    sortable: true,
    filterable: true,
    minWidth: 110,
  },
  {
    id: "failureReason",
    header: "Failure Reason",
    accessor: readFailure,
    sortable: true,
    filterable: true,
    minWidth: 280,
  },
  {
    id: "correlation",
    header: "Correlation",
    accessor: readCorrelation,
    sortable: true,
    filterable: true,
    minWidth: 160,
  },
];

export function CmtsOperationalReadinessPage(
  props: CmtsOperationalReadinessPageProps,
) {
  const [checkSearch, setCheckSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState("ALL");
  const [moduleFilter, setModuleFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [resultSearch, setResultSearch] = useState("");
  const [resultStatusFilter, setResultStatusFilter] =
    useState("ALL");

  const [actionRow, setActionRow] = useState<Row | null>(null);
  const [detailRow, setDetailRow] = useState<Row | null>(null);
  const [resultRow, setResultRow] = useState<Row | null>(null);
  const [selectedScenario, setSelectedScenario] =
    useState<Row | null>(null);

  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] =
    useState<ExportFormat>("CSV");
  const [scenarioPage, setScenarioPage] = useState(1);
  const [busyAction, setBusyAction] = useState<string | null>(
    null,
  );
  const [actionResult, setActionResult] =
    useState<ActionResult | null>(null);

  const checks = useMemo(
    () => normalizeRows(props.checks ?? [], "check"),
    [props.checks],
  );

  const results = useMemo(
    () => normalizeRows(props.results ?? [], "result"),
    [props.results],
  );

  const scenarios = useMemo(
    () =>
      extractScenarios(
        props.report,
        props.scenarios ?? [],
      ),
    [props.report, props.scenarios],
  );

  const visibleChecks = useMemo(() => {
    const search = checkSearch.trim().toLowerCase();

    return checks.filter((row) => {
      const text = [
        readCheckCode(row),
        readDomain(row),
        readModule(row),
        readCheckName(row),
        readRawStatus(row),
        readValidator(row),
        readRemediation(row),
      ]
        .join(" ")
        .toLowerCase();

      if (search && !text.includes(search)) {
        return false;
      }

      if (
        domainFilter !== "ALL" &&
        readDomain(row) !== domainFilter
      ) {
        return false;
      }

      if (
        moduleFilter !== "ALL" &&
        readModule(row) !== moduleFilter
      ) {
        return false;
      }

      if (
        statusFilter !== "ALL" &&
        readRawStatus(row) !== statusFilter
      ) {
        return false;
      }

      return true;
    });
  }, [
    checks,
    checkSearch,
    domainFilter,
    moduleFilter,
    statusFilter,
  ]);

  const visibleResults = useMemo(() => {
    const search = resultSearch.trim().toLowerCase();

    return results.filter((row) => {
      const text = [
        readResultCode(row),
        readModule(row),
        readRawStatus(row),
        readFailure(row),
      ]
        .join(" ")
        .toLowerCase();

      if (search && !text.includes(search)) {
        return false;
      }

      return (
        resultStatusFilter === "ALL" ||
        readRawStatus(row) === resultStatusFilter
      );
    });
  }, [results, resultSearch, resultStatusFilter]);

  const readyCount = getStatusCount(checks, results, "READY");
  const warningCount = getStatusCount(
    checks,
    results,
    "WARNING",
  );
  const failedCount =
    getStatusCount(checks, results, "FAILED") +
    getStatusCount(checks, results, "FAILURE");
  const notRunCount = Math.max(
    0,
    checks.length - readyCount - warningCount - failedCount,
  );

  const readinessScore = useMemo(() => {
    const source = results.length ? results : checks;

    const ready = source.filter(
      (row) => readRawStatus(row) === "READY",
    ).length;

    const warning = source.filter(
      (row) => readRawStatus(row) === "WARNING",
    ).length;

    return Math.round(
      ((ready + warning * 0.65) /
        Math.max(1, source.length)) *
        100,
    );
  }, [checks, results]);

  const domains = uniqueValues(checks.map(readDomain));
  const modules = uniqueValues(checks.map(readModule));
  const statuses = uniqueValues(checks.map(readRawStatus));
  const resultStatuses = uniqueValues(
    results.map(readRawStatus),
  );

  const scenarioPageSize = 5;
  const scenarioPageCount = Math.max(
    1,
    Math.ceil(scenarios.length / scenarioPageSize),
  );
  const safeScenarioPage = Math.min(
    scenarioPage,
    scenarioPageCount,
  );
  const pagedScenarios = scenarios.slice(
    (safeScenarioPage - 1) * scenarioPageSize,
    safeScenarioPage * scenarioPageSize,
  );

  const actions: readonly CmtsGridRowAction<Row>[] = [
    {
      id: "readiness-actions",
      label: "⋮ Actions",
      onClick: setActionRow,
    },
  ];

  async function runCheck(row: Row) {
    if (!props.onRunCheck) {
      return;
    }

    setActionRow(null);
    setBusyAction("run-check");

    try {
      await props.onRunCheck(row);

      setActionResult({
        success: true,
        title: "Readiness check completed",
        message: `${readCheckName(
          row,
        )} was executed successfully.`,
      });
    } catch (error) {
      setActionResult({
        success: false,
        title: "Readiness check failed",
        message: getErrorMessage(error),
      });
    } finally {
      setBusyAction(null);
    }
  }

  async function runAllChecks() {
    if (!props.onRunAll) {
      return;
    }

    setBusyAction("run-all");

    try {
      await props.onRunAll();

      setActionResult({
        success: true,
        title: "All readiness checks completed",
        message:
          "The readiness checks were executed and the latest results were refreshed.",
      });
    } catch (error) {
      setActionResult({
        success: false,
        title: "Readiness execution failed",
        message: getErrorMessage(error),
      });
    } finally {
      setBusyAction(null);
    }
  }

  async function createTask(row: Row) {
    if (!props.onCreateTask) {
      return;
    }

    setActionRow(null);
    setBusyAction("create-task");

    try {
      await props.onCreateTask(row);

      setActionResult({
        success: true,
        title: "Remediation task created",
        message: `A remediation task was created for ${readCheckName(
          row,
        )}.`,
      });
    } catch (error) {
      setActionResult({
        success: false,
        title: "Task creation failed",
        message: getErrorMessage(error),
      });
    } finally {
      setBusyAction(null);
    }
  }

  function openLatestResult(row: Row) {
    setActionRow(null);

    const checkId = String(
      row.id ?? row.checkId ?? row.code ?? "",
    );
    const checkCode = readCheckCode(row);

    const latestResult = results.find((item) => {
      const resultCheckId = String(
        item.checkId ??
          item.readinessCheckId ??
          item.linkedEntityId ??
          "",
      );

      return (
        (checkId && resultCheckId === checkId) ||
        readResultCode(item) === checkCode ||
        String(item.checkCode ?? "") === checkCode
      );
    });

    setResultRow(latestResult ?? row);
  }

  return (
    <div className="cmts-readiness-blue">
      <PageContainer
        title="Operation Readiness"
        subtitle="Executable readiness checks for audit, security, backend endpoints, offline, workflow and governance."
      >
        {props.diagnostics?.length ? (
          <RuntimeDiagnosticBanner
            diagnostics={props.diagnostics}
            title="Operational readiness diagnostics"
          />
        ) : null}

        <CmtsPageHintPanel>
          Readiness checks are repository-backed and executable.
          Run actions persist results, create audit events and
          generate remediation tasks for warning or failed checks.
        </CmtsPageHintPanel>

        <section className="cmts-readiness-hero">
          <div className="cmts-readiness-hero__content">
            <span className="cmts-readiness-eyebrow">
              OPERATION GOVERNANCE
            </span>

            <h2>Operational readiness center</h2>

            <p>
              Monitor executable checks, latest results,
              remediation activity and legacy compatibility
              scenarios from one governed workspace.
            </p>
          </div>

          <div className="cmts-readiness-score-ring">
            <div
              className="cmts-readiness-score-ring__progress"
              style={{
                background: `conic-gradient(#315cf4 ${readinessScore}%, #dfe8f7 ${readinessScore}% 100%)`,
              }}
            >
              <div>
                <strong>{readinessScore}%</strong>
                <span>Readiness</span>
              </div>
            </div>
          </div>

          <div className="cmts-readiness-hero__actions">
            <button
              type="button"
              className="cmts-readiness-primary-button"
              disabled={
                !props.onRunAll ||
                busyAction === "run-all"
              }
              onClick={() => void runAllChecks()}
            >
              ▶{" "}
              {busyAction === "run-all"
                ? "Running checks..."
                : "Run all checks"}
            </button>

            <button
              type="button"
              className="cmts-readiness-secondary-button"
              disabled={!props.onRefresh || props.loading}
              onClick={() => void props.onRefresh?.()}
            >
              ↻ Refresh
            </button>
          </div>
        </section>

        <section className="cmts-readiness-summary-grid">
          <SummaryCard
            label="Total checks"
            value={checks.length}
            description="Active executable checks"
            tone="blue"
            icon="⌘"
          />

          <SummaryCard
            label="Ready"
            value={readyCount}
            description="Checks meeting readiness"
            tone="green"
            icon="✓"
          />

          <SummaryCard
            label="Warning"
            value={warningCount}
            description="Partial readiness detected"
            tone="orange"
            icon="!"
          />

          <SummaryCard
            label="Failed"
            value={failedCount}
            description="Checks requiring attention"
            tone="red"
            icon="×"
          />

          <SummaryCard
            label="Not run"
            value={notRunCount}
            description="No persisted result"
            tone="gray"
            icon="—"
          />
        </section>

        <section className="cmts-readiness-section">
          <SectionHeader
            title="Readiness checks"
            description={`Showing ${visibleChecks.length} of ${checks.length} configured checks.`}
            actions={
              <>
                <button
                  type="button"
                  className="cmts-readiness-secondary-button"
                  onClick={() => {
                    setCheckSearch("");
                    setDomainFilter("ALL");
                    setModuleFilter("ALL");
                    setStatusFilter("ALL");
                  }}
                >
                  Clear filters
                </button>

                <button
                  type="button"
                  className="cmts-readiness-primary-button"
                  disabled={!props.onRunAll}
                  onClick={() => void runAllChecks()}
                >
                  ▶ Run all
                </button>
              </>
            }
          />

          <div className="cmts-readiness-filters">
            <FilterInput
              label="Search"
              value={checkSearch}
              placeholder="Search check, module, validator..."
              onChange={setCheckSearch}
              wide
            />

            <FilterSelect
              label="Domain"
              value={domainFilter}
              options={domains}
              allLabel="All domains"
              onChange={setDomainFilter}
            />

            <FilterSelect
              label="Module"
              value={moduleFilter}
              options={modules}
              allLabel="All modules"
              onChange={setModuleFilter}
            />

            <FilterSelect
              label="Readiness"
              value={statusFilter}
              options={statuses}
              allLabel="All statuses"
              onChange={setStatusFilter}
            />
          </div>

          {props.error ? (
            <ErrorPanel error={props.error} />
          ) : null}

          <div className="cmts-readiness-grid">
            <CmtsSmartDataGrid<Row>
              title={`${visibleChecks.length} readiness checks`}
              rows={visibleChecks}
              columns={checkColumns}
              rowKey="id"
              defaultPageSize={10}
              pageSizeOptions={[10, 25, 50]}
              sortable
              loading={props.loading}
              error={props.error}
              actions={actions}
              onRowClick={setDetailRow}
              emptyMessage="No readiness checks match the selected filters."
            />
          </div>
        </section>

        <section className="cmts-readiness-section">
          <SectionHeader
            title="Readiness results"
            description={`Showing ${visibleResults.length} of ${results.length} persisted results.`}
            actions={
              <button
                type="button"
                className="cmts-readiness-primary-button"
                disabled={visibleResults.length === 0}
                onClick={() => setExportOpen(true)}
              >
                ⇩ Export results
              </button>
            }
          />

          <div className="cmts-readiness-results-filters">
            <FilterInput
              label="Search"
              value={resultSearch}
              placeholder="Search result, module, failure..."
              onChange={setResultSearch}
              wide
            />

            <FilterSelect
              label="Status"
              value={resultStatusFilter}
              options={resultStatuses}
              allLabel="All statuses"
              onChange={setResultStatusFilter}
            />
          </div>

          <div className="cmts-readiness-grid">
            <CmtsSmartDataGrid<Row>
              title={`${visibleResults.length} readiness results`}
              rows={visibleResults}
              columns={resultColumns}
              rowKey="id"
              defaultPageSize={10}
              pageSizeOptions={[10, 25, 50]}
              sortable
              onRowClick={setResultRow}
              emptyMessage="No persisted readiness results are available."
            />
          </div>
        </section>

        <section className="cmts-readiness-section">
          <SectionHeader
            title="Legacy readiness compatibility"
            description="Compatibility scenarios are presented as readable records instead of raw JSON."
          />

          <ReportSummary
            report={props.report}
            scenarioCount={scenarios.length}
          />

          {pagedScenarios.length ? (
            <div className="cmts-readiness-scenario-list">
              {pagedScenarios.map((scenario, index) => (
                <article
                  key={String(
                    scenario.id ??
                      scenario.scenarioCode ??
                      index,
                  )}
                  className="cmts-readiness-scenario-card"
                >
                  <div className="cmts-readiness-scenario-card__number">
                    {(safeScenarioPage - 1) *
                      scenarioPageSize +
                      index +
                      1}
                  </div>

                  <div className="cmts-readiness-scenario-card__content">
                    <span>
                      {readScenarioCode(scenario)}
                    </span>
                    <h4>{readScenarioName(scenario)}</h4>
                    <p>
                      {humanize(
                        scenario.workflowProfileCode ??
                          scenario.workflowCode,
                        "Workflow not specified",
                      )}
                    </p>
                  </div>

                  <div className="cmts-readiness-scenario-card__meta">
                    <span>
                      {readLifecycleSteps(scenario).length} steps
                    </span>

                    <span>
                      {readBlockingErrors(scenario).length} blockers
                    </span>
                  </div>

                  <button
                    type="button"
                    className="cmts-readiness-secondary-button"
                    onClick={() =>
                      setSelectedScenario(scenario)
                    }
                  >
                    View scenario
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="cmts-readiness-empty">
              No legacy compatibility scenarios were returned.
            </div>
          )}

          {scenarios.length > scenarioPageSize ? (
            <Pagination
              page={safeScenarioPage}
              pageCount={scenarioPageCount}
              onPageChange={setScenarioPage}
            />
          ) : null}
        </section>
      </PageContainer>

      {actionRow ? (
        <ReadinessActionsModal
          row={actionRow}
          busy={Boolean(busyAction)}
          canRun={Boolean(props.onRunCheck)}
          canCreateTask={Boolean(props.onCreateTask)}
          onClose={() => setActionRow(null)}
          onRun={() => void runCheck(actionRow)}
          onTask={() => void createTask(actionRow)}
          onDetail={() => {
            setActionRow(null);
            setDetailRow(actionRow);
          }}
          onResult={() => openLatestResult(actionRow)}
        />
      ) : null}

      {detailRow ? (
        <CheckDetailModal
          row={detailRow}
          onClose={() => setDetailRow(null)}
        />
      ) : null}

      {resultRow ? (
        <ResultDetailModal
          row={resultRow}
          onClose={() => setResultRow(null)}
        />
      ) : null}

      {selectedScenario ? (
        <ScenarioDetailModal
          scenario={selectedScenario}
          onClose={() => setSelectedScenario(null)}
        />
      ) : null}

      {actionResult ? (
        <ActionResultModal
          result={actionResult}
          onClose={() => setActionResult(null)}
        />
      ) : null}

      {exportOpen ? (
        <ExportModal
          recordCount={visibleResults.length}
          format={exportFormat}
          onFormatChange={setExportFormat}
          onClose={() => setExportOpen(false)}
          onExport={() => {
            downloadResults(
              visibleResults,
              exportFormat,
            );
            setExportOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function SummaryCard(props: {
  readonly label: string;
  readonly value: number;
  readonly description: string;
  readonly tone: string;
  readonly icon: string;
}) {
  return (
    <article
      className={`cmts-readiness-summary-card cmts-readiness-summary-card--${props.tone}`}
    >
      <div>
        <span>{props.label}</span>
        <i>{props.icon}</i>
      </div>

      <strong>{props.value}</strong>
      <p>{props.description}</p>
    </article>
  );
}

function SectionHeader(props: {
  readonly title: string;
  readonly description: string;
  readonly actions?: ReactNode;
}) {
  return (
    <header className="cmts-readiness-section__header">
      <div>
        <h3>{props.title}</h3>
        <p>{props.description}</p>
      </div>

      {props.actions ? (
        <div className="cmts-readiness-section__actions">
          {props.actions}
        </div>
      ) : null}
    </header>
  );
}

function FilterInput(props: {
  readonly label: string;
  readonly value: string;
  readonly placeholder: string;
  readonly wide?: boolean;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label
      className={`cmts-readiness-filter${
        props.wide ? " cmts-readiness-filter--wide" : ""
      }`}
    >
      <span>{props.label}</span>

      <input
        type="search"
        value={props.value}
        placeholder={props.placeholder}
        onChange={(event) =>
          props.onChange(event.target.value)
        }
      />
    </label>
  );
}

function FilterSelect(props: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly string[];
  readonly allLabel: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="cmts-readiness-filter">
      <span>{props.label}</span>

      <select
        value={props.value}
        onChange={(event) =>
          props.onChange(event.target.value)
        }
      >
        <option value="ALL">{props.allLabel}</option>

        {props.options.map((option) => (
          <option key={option} value={option}>
            {humanize(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function ErrorPanel(props: { readonly error: string }) {
  return (
    <div className="cmts-readiness-error">
      <strong>Unable to load readiness data</strong>
      <span>{props.error}</span>
    </div>
  );
}

function ReadinessActionsModal(props: {
  readonly row: Row;
  readonly busy: boolean;
  readonly canRun: boolean;
  readonly canCreateTask: boolean;
  readonly onClose: () => void;
  readonly onRun: () => void;
  readonly onTask: () => void;
  readonly onDetail: () => void;
  readonly onResult: () => void;
}) {
  return (
    <ModalShell
      title="Readiness actions"
      subtitle={readCheckName(props.row)}
      onClose={props.onClose}
    >
      <div className="cmts-audit-action-list">
        <ActionOption
          icon="▶"
          title="Run check"
          description="Execute this persisted readiness check."
          disabled={!props.canRun || props.busy}
          onClick={props.onRun}
        />

        <ActionOption
          icon="+"
          title="Create remediation task"
          description="Create a task linked to this readiness check."
          disabled={!props.canCreateTask || props.busy}
          onClick={props.onTask}
        />

        <ActionOption
          icon="◉"
          title="View check details"
          description="Review validator, readiness and remediation details."
          onClick={props.onDetail}
        />

        <ActionOption
          icon="↗"
          title="View latest result"
          description="Review the latest persisted execution result."
          onClick={props.onResult}
        />
      </div>
    </ModalShell>
  );
}

function ActionOption(props: {
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="cmts-audit-action-option"
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <span className="cmts-audit-action-option__icon">
        {props.icon}
      </span>

      <span>
        <strong>{props.title}</strong>
        <small>{props.description}</small>
      </span>

      <span className="cmts-audit-action-option__arrow">
        ›
      </span>
    </button>
  );
}

function CheckDetailModal(props: {
  readonly row: Row;
  readonly onClose: () => void;
}) {
  const items = [
    ["Check Code", readCheckCode(props.row)],
    ["Check Name", readCheckName(props.row)],
    ["Domain", readDomain(props.row)],
    ["Module", readModule(props.row)],
    ["Readiness", readStatus(props.row)],
    ["Validator", readValidator(props.row)],
    ["Remediation", readRemediation(props.row)],
  ] as const;

  return (
    <DetailModal
      title="Readiness check details"
      subtitle={readCheckCode(props.row)}
      items={items}
      onClose={props.onClose}
    />
  );
}

function ResultDetailModal(props: {
  readonly row: Row;
  readonly onClose: () => void;
}) {
  const items = [
    ["Result Code", readResultCode(props.row)],
    ["Ran At", readRanAt(props.row)],
    ["Module", readModule(props.row)],
    ["Status", readStatus(props.row)],
    ["Score", readScore(props.row)],
    ["Failure Reason", readFailure(props.row)],
    ["Correlation", readCorrelation(props.row)],
  ] as const;

  return (
    <DetailModal
      title="Readiness result"
      subtitle={readResultCode(props.row)}
      items={items}
      onClose={props.onClose}
    />
  );
}

function DetailModal(props: {
  readonly title: string;
  readonly subtitle: string;
  readonly items: readonly (
    readonly [string, string]
  )[];
  readonly onClose: () => void;
}) {
  return (
    <ModalShell
      title={props.title}
      subtitle={props.subtitle}
      onClose={props.onClose}
      wide
    >
      <div className="cmts-readiness-detail-grid">
        {props.items.map(([label, value]) => (
          <div
            key={label}
            className="cmts-readiness-detail-item"
          >
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Done
        </button>
      </div>
    </ModalShell>
  );
}

function ScenarioDetailModal(props: {
  readonly scenario: Row;
  readonly onClose: () => void;
}) {
  const [stepPage, setStepPage] = useState(1);
  const steps = readLifecycleSteps(props.scenario);
  const blockers = readBlockingErrors(props.scenario);
  const pageSize = 5;
  const pageCount = Math.max(
    1,
    Math.ceil(steps.length / pageSize),
  );
  const safePage = Math.min(stepPage, pageCount);
  const pagedSteps = steps.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  return (
    <ModalShell
      title={readScenarioName(props.scenario)}
      subtitle={readScenarioCode(props.scenario)}
      onClose={props.onClose}
      wide
    >
      <div className="cmts-readiness-scenario-summary">
        <ScenarioValue
          label="Workflow Profile"
          value={humanize(
            props.scenario.workflowProfileCode,
            "Not configured",
          )}
        />

        <ScenarioValue
          label="SLA Profile"
          value={humanize(
            props.scenario.slaProfileCode,
            "Not configured",
          )}
        />

        <ScenarioValue
          label="Escalation Profile"
          value={humanize(
            props.scenario.escalationProfileCode,
            "Not configured",
          )}
        />

        <ScenarioValue
          label="Routing Rule"
          value={humanize(
            props.scenario.routingRuleCode,
            "Not configured",
          )}
        />
      </div>

      <h4 className="cmts-readiness-modal-heading">
        Lifecycle steps
      </h4>

      <div className="cmts-readiness-step-list">
        {pagedSteps.map((step, index) => (
          <article
            key={String(step.code ?? step.sequence ?? index)}
            className="cmts-readiness-step-card"
          >
            <span className="cmts-readiness-step-card__number">
              {String(
                step.sequence ??
                  (safePage - 1) * pageSize +
                    index +
                    1,
              )}
            </span>

            <div>
              <strong>
                {humanize(
                  step.name ?? step.code,
                  "Lifecycle Step",
                )}
              </strong>

              <small>
                {humanize(
                  step.code,
                  "No step code",
                )}
              </small>
            </div>

            <div className="cmts-readiness-chip-list">
              {readStringArray(step.requiredInputs).map(
                (input) => (
                  <span key={input}>{input}</span>
                ),
              )}
            </div>
          </article>
        ))}
      </div>

      {steps.length > pageSize ? (
        <Pagination
          page={safePage}
          pageCount={pageCount}
          onPageChange={setStepPage}
        />
      ) : null}

      <h4 className="cmts-readiness-modal-heading">
        Blocking errors
      </h4>

      {blockers.length ? (
        <div className="cmts-readiness-blockers">
          {blockers.map((blocker) => (
            <span key={blocker}>{blocker}</span>
          ))}
        </div>
      ) : (
        <div className="cmts-readiness-no-blockers">
          ✓ No blocking errors
        </div>
      )}

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Done
        </button>
      </div>
    </ModalShell>
  );
}

function ScenarioValue(props: {
  readonly label: string;
  readonly value: string;
}) {
  return (
    <div>
      <span>{props.label}</span>
      <strong>{props.value}</strong>
    </div>
  );
}

function ReportSummary(props: {
  readonly report: unknown;
  readonly scenarioCount: number;
}) {
  const report = asRecord(props.report);
  const blockers = readStringArray(
    report.blockingErrors ??
      report.errors ??
      report.failures,
  );

  return (
    <div className="cmts-readiness-report-summary">
      <ScenarioValue
        label="Report Status"
        value={humanize(
          report.status,
          "Compatibility report available",
        )}
      />

      <ScenarioValue
        label="Scenarios"
        value={String(props.scenarioCount)}
      />

      <ScenarioValue
        label="Blocking Errors"
        value={String(blockers.length)}
      />

      <ScenarioValue
        label="Generated At"
        value={formatDateTime(
          report.generatedAt ??
            report.createdAt ??
            report.ranAt,
        )}
      />
    </div>
  );
}

function Pagination(props: {
  readonly page: number;
  readonly pageCount: number;
  readonly onPageChange: (page: number) => void;
}) {
  return (
    <div className="cmts-readiness-pagination">
      <button
        type="button"
        disabled={props.page <= 1}
        onClick={() =>
          props.onPageChange(props.page - 1)
        }
      >
        Previous
      </button>

      <span>
        Page {props.page} of {props.pageCount}
      </span>

      <button
        type="button"
        disabled={props.page >= props.pageCount}
        onClick={() =>
          props.onPageChange(props.page + 1)
        }
      >
        Next
      </button>
    </div>
  );
}

function ActionResultModal(props: {
  readonly result: ActionResult;
  readonly onClose: () => void;
}) {
  return (
    <ModalShell
      title={props.result.title}
      onClose={props.onClose}
    >
      <div
        className={`cmts-readiness-action-result ${
          props.result.success
            ? "cmts-readiness-action-result--success"
            : "cmts-readiness-action-result--failure"
        }`}
      >
        <span>{props.result.success ? "✓" : "!"}</span>

        <div>
          <strong>
            {props.result.success
              ? "Operation completed"
              : "Operation failed"}
          </strong>
          <p>{props.result.message}</p>
        </div>
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
}

function ExportModal(props: {
  readonly recordCount: number;
  readonly format: ExportFormat;
  readonly onFormatChange: (format: ExportFormat) => void;
  readonly onClose: () => void;
  readonly onExport: () => void;
}) {
  const formats: readonly ExportFormat[] = [
    "CSV",
    "XLS",
    "JSON",
  ];

  return (
    <ModalShell
      title="Export readiness results"
      subtitle="Download the currently filtered result records."
      onClose={props.onClose}
      wide
    >
      <div className="cmts-export-banner">
        <span className="cmts-export-banner__icon">⇩</span>

        <div>
          <small>DATA EXPORT</small>
          <strong>Prepare your export</strong>
          <p>
            Export readable result, status, score and failure
            information.
          </p>
        </div>

        <span className="cmts-export-record-count">
          {props.recordCount} records
        </span>
      </div>

      <div className="cmts-export-format-grid">
        {formats.map((format) => {
          const selected = format === props.format;

          return (
            <button
              type="button"
              key={format}
              className={`cmts-export-format-card${
                selected
                  ? " cmts-export-format-card--selected"
                  : ""
              }`}
              onClick={() =>
                props.onFormatChange(format)
              }
            >
              <span className="cmts-export-format-badge">
                {format}
              </span>

              <span
                className={`cmts-export-radio${
                  selected
                    ? " cmts-export-radio--selected"
                    : ""
                }`}
              >
                {selected ? "✓" : ""}
              </span>

              <strong>
                {format === "CSV"
                  ? "CSV spreadsheet"
                  : format === "XLS"
                    ? "Excel workbook"
                    : "JSON data"}
              </strong>

              <small>
                Download the filtered readiness result values.
              </small>
            </button>
          );
        })}
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-secondary-button"
          onClick={props.onClose}
        >
          Cancel
        </button>

        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onExport}
        >
          Download {props.format}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell(props: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
  readonly wide?: boolean;
}) {
  return (
    <div
      className="cmts-audit-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          props.onClose();
        }
      }}
    >
      <section
        className={`cmts-audit-modal${
          props.wide ? " cmts-audit-modal--wide" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
      >
        <header className="cmts-audit-modal__header">
          <div>
            <h3>{props.title}</h3>
            {props.subtitle ? (
              <p>{props.subtitle}</p>
            ) : null}
          </div>

          <button
            type="button"
            className="cmts-audit-modal__close"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>

        <div className="cmts-audit-modal__body">
          {props.children}
        </div>
      </section>
    </div>
  );
}

function normalizeRows(
  value: unknown,
  prefix: string,
): readonly Row[] {
  const rows = extractRowCollection(value);

  return rows.map((row, index) => ({
    ...row,
    id:
      row.id ??
      row.checkId ??
      row.resultId ??
      row.readinessCheckId ??
      row.readinessResultId ??
      row.code ??
      `${prefix}-${index + 1}`,
  }));
}

function extractRowCollection(
  value: unknown,
  depth = 0,
): readonly Row[] {
  if (depth > 5) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(isRecord);
  }

  if (!isRecord(value)) {
    return [];
  }

  const collectionKeys = [
    "items",
    "rows",
    "records",
    "checks",
    "results",
    "values",
    "data",
    "result",
    "payload",
  ] as const;

  for (const key of collectionKeys) {
    const candidate = value[key];

    if (Array.isArray(candidate)) {
      return candidate.filter(isRecord);
    }

    if (isRecord(candidate)) {
      const nestedRows = extractRowCollection(
        candidate,
        depth + 1,
      );

      if (nestedRows.length) {
        return nestedRows;
      }
    }
  }

  if (
    value.id ||
    value.code ||
    value.checkId ||
    value.resultId
  ) {
    return [value];
  }

  return [];
}

function getStatusCount(
  checks: readonly Row[],
  results: readonly Row[],
  status: string,
): number {
  const source = results.length ? results : checks;
  return source.filter(
    (row) => readRawStatus(row) === status,
  ).length;
}

function readCheckCode(row: Row): string {
  return String(
    row.checkCode ?? row.code ?? "CHECK",
  )
    .trim()
    .toUpperCase();
}

function readResultCode(row: Row): string {
  return String(
    row.resultCode ??
      row.code ??
      row.checkCode ??
      "RESULT",
  )
    .trim()
    .toUpperCase();
}

function readDomain(row: Row): string {
  return humanize(
    row.domainName ?? row.domainCode ?? row.domain,
    "General",
  );
}

function readModule(row: Row): string {
  return humanize(
    row.moduleName ?? row.moduleCode ?? row.module,
    "Core",
  );
}

function readCheckName(row: Row): string {
  return humanize(
    row.displayName ?? row.name ?? row.checkName ?? row.code,
    "Readiness Check",
  );
}

function readRawStatus(row: Row): string {
  return String(
    row.statusName ??
      row.status ??
      row.readinessStatus ??
      "NOT_RUN",
  )
    .trim()
    .toUpperCase();
}

function readStatus(row: Row): string {
  const status = readRawStatus(row);

  if (status === "READY" || status === "PASSED") {
    return `🟢 ${humanize(status)}`;
  }

  if (status === "WARNING") {
    return "🟠 Warning";
  }

  if (
    status === "FAILED" ||
    status === "FAILURE"
  ) {
    return `🔴 ${humanize(status)}`;
  }

  if (
    status === "RUNNING" ||
    status === "PENDING"
  ) {
    return `🔵 ${humanize(status)}`;
  }

  return `⚪ ${humanize(status)}`;
}

function readValidator(row: Row): string {
  const value = String(
    row.validatorName ??
      row.validator ??
      row.validatorCode ??
      "",
  ).trim();

  return !value || isUuid(value)
    ? "Backend validator"
    : humanize(value);
}

function readRemediation(row: Row): string {
  const value = String(
    row.remediationSuggestion ??
      row.remediation ??
      row.recommendedAction ??
      "",
  ).trim();

  return value || "No remediation required";
}

function readRanAt(row: Row): string {
  return formatDateTime(
    row.ranAt ??
      row.executedAt ??
      row.createdAt ??
      row.completedAt,
  );
}

function readScore(row: Row): string {
  const numeric = Number(
    row.score ?? row.readinessScore ?? row.percentage,
  );

  return Number.isFinite(numeric)
    ? `${numeric}${numeric <= 1 ? "" : "%"}`
    : "Not scored";
}

function readFailure(row: Row): string {
  const value = String(
    row.failureReason ??
      row.errorMessage ??
      row.failure ??
      "",
  ).trim();

  return value || "No failure recorded";
}

function readCorrelation(row: Row): string {
  return row.correlationId ??
    row.requestId ??
    row.traceId
    ? "🔗 Chain available"
    : "No correlation";
}

function extractScenarios(
  reportValue: unknown,
  provided: readonly Row[],
): readonly Row[] {
  if (provided.length) {
    return normalizeRows(provided, "scenario");
  }

  const report = asRecord(reportValue);
  const data = asRecord(report.data);

  const value =
    report.scenarioEvidence ??
    report.scenarios ??
    data.scenarioEvidence ??
    data.scenarios;

  return Array.isArray(value)
    ? normalizeRows(
        value.filter(isRecord),
        "scenario",
      )
    : [];
}

function readScenarioCode(row: Row): string {
  return String(
    row.scenarioCode ?? row.code ?? "SCENARIO",
  ).toUpperCase();
}

function readScenarioName(row: Row): string {
  return humanize(
    row.scenarioName ?? row.name ?? row.scenarioCode,
    "Compatibility Scenario",
  );
}

function readLifecycleSteps(row: Row): readonly Row[] {
  const value =
    row.lifecycleSteps ??
    row.steps ??
    row.workflowSteps;

  return Array.isArray(value)
    ? value.filter(isRecord)
    : [];
}

function readBlockingErrors(row: Row): readonly string[] {
  return readStringArray(
    row.blockingErrors ??
      row.errors ??
      row.validationErrors,
  );
}

function readStringArray(value: unknown): readonly string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) =>
      typeof item === "object" && item !== null
        ? String(
            (item as Row).message ??
              (item as Row).code ??
              "",
          )
        : String(item ?? ""),
    )
    .filter(Boolean);
}

function asRecord(value: unknown): Row {
  return isRecord(value) ? value : {};
}

function isRecord(value: unknown): value is Row {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

function uniqueValues(
  values: readonly string[],
): readonly string[] {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
}

function humanize(
  value: unknown,
  fallback = "Not available",
): string {
  const text = String(value ?? "").trim();

  if (!text || isUuid(text)) {
    return fallback;
  }

  return text
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (character) =>
      character.toUpperCase(),
    );
}

function formatDateTime(value: unknown): string {
  const text = String(value ?? "").trim();

  if (!text) {
    return "Not recorded";
  }

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) {
    return humanize(text, "Not recorded");
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "The backend operation could not be completed.";
}

function downloadResults(
  rows: readonly Row[],
  format: ExportFormat,
) {
  const exportRows = rows.map((row) => ({
    "Ran At": readRanAt(row),
    "Result Code": readResultCode(row),
    Module: readModule(row),
    Status: readStatus(row).replace(/^[^\s]+\s/, ""),
    Score: readScore(row),
    "Failure Reason": readFailure(row),
    Correlation: readCorrelation(row).replace(
      /^[^\s]+\s/,
      "",
    ),
  }));

  downloadRows(
    exportRows,
    format,
    "readiness-results",
  );
}

function downloadRows(
  rows: readonly Row[],
  format: ExportFormat,
  prefix: string,
) {
  const timestamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace(/[:T]/g, "-");
  const headers = Object.keys(rows[0] ?? {});

  if (format === "JSON") {
    saveBlob(
      JSON.stringify(rows, null, 2),
      `${prefix}-${timestamp}.json`,
      "application/json;charset=utf-8",
    );
    return;
  }

  if (format === "XLS") {
    const html = `<html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr>${headers
      .map((header) => `<th>${escapeHtml(header)}</th>`)
      .join("")}</tr></thead><tbody>${rows
      .map(
        (row) =>
          `<tr>${headers
            .map(
              (header) =>
                `<td>${escapeHtml(
                  String(row[header] ?? ""),
                )}</td>`,
            )
            .join("")}</tr>`,
      )
      .join("")}</tbody></table></body></html>`;

    saveBlob(
      html,
      `${prefix}-${timestamp}.xls`,
      "application/vnd.ms-excel;charset=utf-8",
    );
    return;
  }

  const csv = [
    headers.map(csvCell).join(","),
    ...rows.map((row) =>
      headers
        .map((header) => csvCell(row[header]))
        .join(","),
    ),
  ].join("\n");

  saveBlob(
    `\uFEFF${csv}`,
    `${prefix}-${timestamp}.csv`,
    "text/csv;charset=utf-8",
  );
}

function csvCell(value: unknown): string {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function saveBlob(
  content: string,
  fileName: string,
  mimeType: string,
) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}